import { ICMS, ICMSIMAGE } from "@/store/home/homeModel";

export const decodeHtml = (html: string) => {
  if (typeof window === "undefined") {
    // Server-side: basic HTML entity decode
    return (
      html
        ?.replace(/&amp;/g, "&")
        ?.replace(/&lt;/g, "<")
        ?.replace(/&gt;/g, ">")
        ?.replace(/&quot;/g, '"')
        ?.replace(/&#39;/g, "'")
        ?.replace(/&nbsp;/g, " ")
        ?.replace(/^<p>([\s\S]*?)<\/p>$/, "$1") || ""
    );
  }
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  const decodedHtml = txt.value;
  return decodedHtml.replace(/^<p>([\s\S]*?)<\/p>$/, "$1");
};

export const getTicketPrice = (date: string): number => {
  if (!date) return 0;
  const selectedDate = new Date(date);
  const day = selectedDate.getDay();
  return day === 0 || day === 6 ? 1000 : 900;
};

export const getBreakdown = (date: string, passengers: number) => {
  const inclusivePrice = getTicketPrice(date);
  const totalInclusive = inclusivePrice * passengers;
  const basePerTicket = inclusivePrice / 1.18;
  const baseTotal = basePerTicket * passengers;
  const cgst = baseTotal * 0.09;
  const sgst = baseTotal * 0.09;
  const perTicket = inclusivePrice;
  const grandTotal = totalInclusive;
  return { perTicket, cgst, sgst, grandTotal };
};

export function formatIndianNumber(number: number, decimal: any = 2): string {
  if (number === null || number === undefined) return "";
  let [integerPart, decimalPart] = number.toFixed(decimal).split(".");
  let lastThreeDigits = integerPart.slice(-3);
  let otherDigits = integerPart.slice(0, -3);
  if (otherDigits !== "") {
    lastThreeDigits = "," + lastThreeDigits;
  }
  let formattedOtherDigits = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return formattedOtherDigits + lastThreeDigits + "." + decimalPart;
}

/**
 * Strips <title>, <meta>, and <link> tags from HTML strings.
 * This is used to prevent meta tags from the CMS from appearing in the body.
 */
export const cleanHeaderTags = (html: string): string => {
  if (!html) return "";
  return html
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<meta[^>]*>/gi, "")
    .replace(/<\/meta[^>]*>/gi, "")
    .replace(/<link[^>]*>/gi, "")
    .replace(/<\/link[^>]*>/gi, "")
    .replace(/<head[^>]*>/gi, "")
    .replace(/<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "");
};

export function createLookupObject<
  T extends Record<string, any>,
  K extends keyof T,
>(array: T[], key: K): Record<T[K] & (string | number | symbol), T> {
  const lookup: Record<string | number | symbol, T> = {};

  const cleanHeaderItem = array?.map((item: any) => {
    return {
      ...item,
      Template: cleanHeaderTags(item?.Template),
    };
  });

  for (const item of cleanHeaderItem) {
    lookup[item[key] as any] = item;
  }
  return lookup as Record<T[K] & (string | number | symbol), T>;
}

export const processCMSContent = (cms: ICMS): string => {
  if (!cms?.Template) return "";
  let processedContent = decodeHtml(cms.Template);
  if (cms.cms_image_details && cms.cms_image_details.length > 0) {
    cms.cms_image_details.forEach((img: ICMSIMAGE) => {
      const imgNameParts = img.Name?.split("-");
      const imgIdentifier = imgNameParts?.length
        ? imgNameParts[imgNameParts.length - 1]
        : img.Name;
      if (imgIdentifier === "bg") {
        processedContent = processedContent.replace(
          `id="${img.Name}">`,
          `id="${img.Name}" style="background-image:url('${img.Image}')">`,
        );
      } else {
        const placeholderPattern = new RegExp(
          `id="${img.Name}">\\s*</div>`,
          "g",
        );
        processedContent = processedContent.replace(
          placeholderPattern,
          `id="${img.Name}"><img src="${img.Image}" alt="${img.AltName || ""}" class="img-fluid" /></div>`,
        );
      }
    });
  }
  return processedContent;
};
